import AuthenticationLayout from 'components/AuthLayout';
import Button from 'components/Button';
import Space from 'components/Space';
import React, { FC, useCallback, useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import useSWR from 'swr';
import User from 'types/User';
import VerifiedEmailRequired from './user/error/verified-email-required';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  color: var(--gray-900);
  font-size: 1.2rem;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  color: var(--gray-500);
  width: min(90%, 60rem);
  font-size: 1rem;
  font-weight: 400;
  text-align: center;
`;

const Text = styled.p`
  color: var(--gray-700);
  width: min(90%, 60rem);
  font-size: 1rem;
  margin-bottom: 1rem;
  font-weight: 400;
`;

const ButtonsContainer = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
`;

type ContextType = {
  user: User | undefined;
};
const HomePageContext = React.createContext({} as ContextType);
const { Provider } = HomePageContext;

const HomePage: FC = () => {
  const { data: user, error } = useSWR<User>('/auth/profile');
  const value = useMemo(
    () => ({
      user,
    }),
    [user],
  );

  const Content: FC = useCallback(
    (props) =>
      user ? (
        (user?.UserRole?.some((userRole) => userRole.role === 'MENTEE')
          ? MenteePage(props)
          : null) ||
        (user?.UserRole?.some((userRole) => userRole.role === 'MENTOR')
          ? MentorPage(props)
          : null) || <DefaultPage />
      ) : error?.response?.status === 403 ? (
        <VerifiedEmailRequired />
      ) : (
        <LoginPage />
      ),
    [user, error],
  );

  return (
    <Provider value={value}>
      <AuthenticationLayout src_image="/logo_institut_principal.png">
        <Content />
      </AuthenticationLayout>
    </Provider>
  );
};

const DefaultPage: FC = () => {
  return (
    <Container>
      <Title>Bienvenue sur le portail du programme mentorat</Title>
      <Subtitle>Pour commencer, choisissez une page dans le menu.</Subtitle>
    </Container>
  );
};

const LoginPage: FC = () => {
  return (
    <Container>
      <Title>Bienvenue sur le portail du programme mentorat</Title>
      <Subtitle>
        Vous devez être connecté pour accéder à la plateforme.
      </Subtitle>
      <ButtonsContainer>
        <Link to="/authentication/signup">
          <Button variant="outlined">S'inscrire</Button>
        </Link>
        <Link to="/authentication/login">
          <Button color="primary">Se connecter</Button>
        </Link>
      </ButtonsContainer>
    </Container>
  );
};

const MenteePage: FC = () => {
  const { user } = useContext(HomePageContext);
  switch (user?.menteeStatus) {
    case 'FILLING_FORM':
      return (
        <Container>
          <Title>Bienvenue sur le portail du programme mentorat</Title>
          <Text>
            Ce programme vise à accompagner de jeunes engagés et les aide à
            valoriser leur engagement et à construire leurs projets.
          </Text>
          <Text>
            Afin d’être mis en relation avec votre mentor, merci de remplir le
            petit questionnaire et de signer la charte d'engagement.
          </Text>

          <Text>Merci pour votre engagement !</Text>
          <Link to="/mentee/fill-form">
            <Button color="primary">Compléter le formulaire de mentorat</Button>
          </Link>
        </Container>
      );
    case 'SIGNING_CHART':
      return (
        <Container>
          <Title>Bienvenue sur le portail du programme mentorat</Title>
          <Text>
            Ce programme vise à accompagner de jeunes engagés et les aide à
            valoriser leur engagement et à construire leurs projets.
          </Text>
          <Text>
            Afin d’être mis en relation avec votre mentor, merci de remplir le
            petit questionnaire et de signer la charte d'engagement.
          </Text>

          <Text>Merci pour votre engagement !</Text>
          <Link to="/mentee/sign-chart">
            <Button color="primary">Signer la charte</Button>
          </Link>
        </Container>
      );
    default:
      return (
        <Container>
          <Title>Bienvenue sur le portail du programme mentorat</Title>
          <Subtitle>Pour commencer, choisissez une page dans le menu.</Subtitle>
          <Space height={2} />
        </Container>
      );
  }
};

const MentorPage: FC = () => {
  const { user } = useContext(HomePageContext);
  switch (user?.mentorStatus) {
    case 'FILLING_FORM':
      return (
        <Container>
          <Title>Bienvenue sur le portail du programme mentorat</Title>
          <Text>
            Ce programme vise à accompagner de jeunes engagés et les aide à
            valoriser leur engagement et à construire leurs projets.
          </Text>
          <Text>
            Afin d’être mis en relation avec votre ou vos mentorés, merci de
            remplir le petit questionnaire et de signer la charte d'engagement.
          </Text>

          <Text>Merci pour votre engagement !</Text>
          <Link to="/mentor/fill-form">
            <Button color="primary">Compléter le formulaire de mentorat</Button>
          </Link>
        </Container>
      );
    case 'SIGNING_CHART':
      return (
        <Container>
          <Title>Bienvenue sur le portail du programme mentorat</Title>
          <Text>
            Ce programme vise à accompagner de jeunes engagés et les aide à
            valoriser leur engagement et à construire leurs projets.
          </Text>
          <Text>
            Afin d’être mis en relation avec votre ou vos mentorés, merci de
            remplir le petit questionnaire et de signer la charte d'engagement.
          </Text>

          <Text>Merci pour votre engagement !</Text>
          <Link to="/mentor/sign-chart">
            <Button color="primary">Signer la charte</Button>
          </Link>
        </Container>
      );
    case 'TRAINING':
      return (
        <Container>
          <Title>Bienvenue sur le portail du programme mentorat</Title>
          <Text>
            Afin d’être mis en relation avec votre ou vos mentorés, merci de
            participer à la formation que nous vous proposons.
          </Text>

          <Text>Merci pour votre engagement !</Text>
          <Link to="/mentor/mentorship/confirm-training">
            <Button color="primary">Accéder à la formation</Button>
          </Link>
        </Container>
      );
    default:
      return (
        <Container>
          <Title>Bienvenue sur le portail du programme mentorat</Title>
          <Subtitle>Pour commencer, choisissez une page dans le menu.</Subtitle>
          <Space height={2} />
        </Container>
      );
  }
};

export default HomePage;
